import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { DayPicker } from 'react-day-picker';

import { ptBR } from 'date-fns/locale';

import 'react-day-picker/dist/style.css';
import * as Popover from '@radix-ui/react-popover';

import * as S from './styles';

const InputDate = React.forwardRef(
  (
    {
      name,
      value,
      placeholder = 'Selecione uma data',
      label,
      error,
      disabled = false,
      onChange,
      styleContainer,
      required = true,
      ...rest
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [displayValue, setDisplayValue] = useState('');
    const inputRef = useRef(null);

    useImperativeHandle(
      ref,
      () => {
        return {
          focus() {
            inputRef.current.focus();
          },
          scrollIntoView() {
            inputRef.current.scrollIntoView();
          },
        };
      },
      [],
    );

    // Parse initial value
    useEffect(() => {
      if (value) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            setSelectedDate(date);
            updateDisplayValue(date);
          }
        } catch (error) {
          console.warn('Invalid date value:', value);
          setSelectedDate(null);
          setDisplayValue('');
        }
      } else {
        setSelectedDate(null);
        setDisplayValue('');
      }
    }, [value]);

    const updateDisplayValue = date => {
      if (date && !isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        setDisplayValue(`${day}/${month}/${year}`);
      } else {
        setDisplayValue('');
      }
    };

    const handleDaySelect = day => {
      if (disabled) return;
      setSelectedDate(day);
    };

    const handleApply = () => {
      if (disabled) return;

      if (selectedDate) {
        const newDate = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
        );

        updateDisplayValue(newDate);

        if (onChange) {
          onChange(newDate);
        }
      } else {
        setDisplayValue('');
        if (onChange) {
          onChange(null);
        }
      }

      setIsOpen(false);
    };

    const handleCancel = () => {
      if (value) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          setSelectedDate(date);
        }
      } else {
        setSelectedDate(null);
      }

      setIsOpen(false);
    };

    const handleInputClick = () => {
      if (!disabled) {
        setIsOpen(true);
      }
    };

    const handleKeyDown = e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleInputClick();
      }
    };

    return (
      <S.InputDate disabled={disabled} style={styleContainer}>
        {label && <S.Label htmlFor={name}>{label}</S.Label>}

        <S.InputContainer>
          <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger asChild>
              <S.Input
                ref={inputRef}
                id={name}
                name={name}
                value={displayValue || undefined}
                type="text"
                placeholder={placeholder}
                disabled={disabled}
                readOnly
                onClick={handleInputClick}
                onKeyDown={handleKeyDown}
                required={required}
                {...rest}
              />
            </Popover.Trigger>

            <Popover.Portal>
              <S.PopoverContent align="start" sideOffset={4}>
                <S.CalendarSection>
                  <DayPicker
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDaySelect}
                    disabled={disabled}
                    showOutsideDays
                    captionLayout="dropdown"
                    locale={ptBR}
                  />
                </S.CalendarSection>

                <S.ButtonSection>
                  <S.Button variant="cancel" onClick={handleCancel} disabled={disabled}>
                    Cancelar
                  </S.Button>
                  <S.Button onClick={handleApply} disabled={disabled}>
                    Aplicar
                  </S.Button>
                </S.ButtonSection>
              </S.PopoverContent>
            </Popover.Portal>
          </Popover.Root>

          {error && <S.Error>{error}</S.Error>}
        </S.InputContainer>
      </S.InputDate>
    );
  },
);

InputDate.displayName = 'InputDate';

export default InputDate;
